import { Platform } from 'react-native';
import NetInfo from '@react-native-community/netinfo';

export enum TimeoutErrorCode {
  SUPABASE_QUERY = 'TIMEOUT_SUPABASE_QUERY',
  GEOIP_REQUEST = 'TIMEOUT_GEOIP_REQUEST',
  CONTENT_FETCH = 'TIMEOUT_CONTENT_FETCH',
  VIEWING_PROGRESS = 'TIMEOUT_VIEWING_PROGRESS',
  RESUME_EPISODE = 'TIMEOUT_RESUME_EPISODE',
  SERIES_EPISODES = 'TIMEOUT_SERIES_EPISODES',
  NETWORK_RETRY = 'TIMEOUT_NETWORK_RETRY',
  REVENUECAT_INIT = 'TIMEOUT_REVENUECAT_INIT',
  REVENUECAT_HEALTH_REPORT = 'TIMEOUT_REVENUECAT_HEALTH_REPORT',
  REVENUECAT_CUSTOMER_INFO = 'TIMEOUT_REVENUECAT_CUSTOMER_INFO',
  REVENUECAT_OFFERINGS = 'TIMEOUT_REVENUECAT_OFFERINGS',
  REVENUECAT_LOGIN = 'TIMEOUT_REVENUECAT_LOGIN',
  GENERIC = 'TIMEOUT_GENERIC',
}

export enum TimeoutSeverity {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  NON_CRITICAL = 'NON_CRITICAL',
}

export interface TimeoutContext {
  requestId: string;
  operationName: string;
  timeoutDuration: number;
  actualDuration: number;
  networkQuality?: string;
  platform: string;
  errorCode: TimeoutErrorCode;
  severity: TimeoutSeverity;
  timestamp: string;
  stackTrace?: string;
  metadata?: Record<string, any>;
  retryAttempt?: number;
  userId?: string;
  campaignId?: string;
}

class TimeoutLogger {
  private requestCounter = 0;
  private timeoutHistory: TimeoutContext[] = [];
  private maxHistorySize = 100;
  private sessionStartTime = Date.now();

  generateRequestId(): string {
    this.requestCounter++;
    return `REQ_${Date.now()}_${this.requestCounter}`;
  }

  private async getNetworkInfo(): Promise<{ quality: string; details: string }> {
    try {
      const state = await NetInfo.fetch();

      if (!state.isConnected) {
        return { quality: 'OFFLINE', details: 'No connection' };
      }

      if (state.type === 'wifi') {
        return { quality: 'EXCELLENT', details: 'WiFi' };
      } else if (state.type === 'cellular') {
        const generation = (state.details as any)?.cellularGeneration || 'unknown';
        if (generation === '5g') return { quality: 'EXCELLENT', details: '5G' };
        if (generation === '4g') return { quality: 'GOOD', details: '4G' };
        if (generation === '3g') return { quality: 'POOR', details: '3G' };
        return { quality: 'POOR', details: `Cellular (${generation})` };
      }

      return { quality: 'GOOD', details: state.type || 'unknown' };
    } catch (error) {
      return { quality: 'UNKNOWN', details: 'Error detecting network' };
    }
  }

  async logTimeout(context: Omit<TimeoutContext, 'timestamp' | 'networkQuality' | 'platform'>): Promise<void> {
    const timestamp = new Date().toISOString();
    const networkInfo = await this.getNetworkInfo();

    const fullContext: TimeoutContext = {
      ...context,
      timestamp,
      networkQuality: `${networkInfo.quality} (${networkInfo.details})`,
      platform: Platform.OS,
    };

    this.timeoutHistory.push(fullContext);

    if (this.timeoutHistory.length > this.maxHistorySize) {
      this.timeoutHistory.shift();
    }

    this.printTimeoutLog(fullContext);
  }

  private printTimeoutLog(context: TimeoutContext): void {
    const severityEmoji = {
      [TimeoutSeverity.CRITICAL]: '🔴',
      [TimeoutSeverity.HIGH]: '🟠',
      [TimeoutSeverity.MEDIUM]: '🟡',
      [TimeoutSeverity.LOW]: '🟢',
      [TimeoutSeverity.NON_CRITICAL]: '⚪',
    };

    const icon = severityEmoji[context.severity] || '⚠️';

    console.log('╔════════════════════════════════════════════════════════════════');
    console.log(`║ ${icon} TIMEOUT ERROR DETECTED`);
    console.log('╠════════════════════════════════════════════════════════════════');
    console.log(`║ Request ID:     ${context.requestId}`);
    console.log(`║ Error Code:     ${context.errorCode}`);
    console.log(`║ Severity:       ${context.severity}`);
    console.log(`║ Operation:      ${context.operationName}`);
    console.log('╠────────────────────────────────────────────────────────────────');
    console.log(`║ Timeout Config: ${context.timeoutDuration}ms`);
    console.log(`║ Actual Duration: ${context.actualDuration}ms`);
    console.log(`║ Exceeded By:    ${context.actualDuration - context.timeoutDuration}ms`);
    console.log('╠────────────────────────────────────────────────────────────────');
    console.log(`║ Network:        ${context.networkQuality}`);
    console.log(`║ Platform:       ${context.platform}`);
    console.log(`║ Timestamp:      ${context.timestamp}`);

    if (context.retryAttempt !== undefined) {
      console.log(`║ Retry Attempt:  ${context.retryAttempt}`);
    }

    if (context.userId) {
      console.log(`║ User ID:        ${context.userId}`);
    }

    if (context.campaignId) {
      console.log(`║ Campaign ID:    ${context.campaignId}`);
    }

    if (context.metadata && Object.keys(context.metadata).length > 0) {
      console.log('╠────────────────────────────────────────────────────────────────');
      console.log('║ Additional Context:');
      Object.entries(context.metadata).forEach(([key, value]) => {
        console.log(`║   ${key}: ${JSON.stringify(value)}`);
      });
    }

    if (context.stackTrace) {
      console.log('╠────────────────────────────────────────────────────────────────');
      console.log('║ Stack Trace:');
      const stackLines = context.stackTrace.split('\n').slice(0, 5);
      stackLines.forEach(line => {
        console.log(`║   ${line}`);
      });
    }

    console.log('╚════════════════════════════════════════════════════════════════');
  }

  logOperationStart(requestId: string, operationName: string, metadata?: Record<string, any>): void {
    const timestamp = new Date().toISOString();
    console.log(`⏱️ [${timestamp}] [${requestId}] START: ${operationName}`, metadata || '');
  }

  logOperationProgress(requestId: string, operationName: string, progress: string, metadata?: Record<string, any>): void {
    const timestamp = new Date().toISOString();
    console.log(`⏳ [${timestamp}] [${requestId}] PROGRESS: ${operationName} - ${progress}`, metadata || '');
  }

  logOperationComplete(requestId: string, operationName: string, duration: number, metadata?: Record<string, any>): void {
    const timestamp = new Date().toISOString();
    console.log(`✅ [${timestamp}] [${requestId}] COMPLETE: ${operationName} (${duration}ms)`, metadata || '');
  }

  logOperationWarning(requestId: string, operationName: string, warning: string, percentComplete: number): void {
    const timestamp = new Date().toISOString();
    console.log(`⚠️ [${timestamp}] [${requestId}] WARNING: ${operationName} - ${warning} (${percentComplete}% of timeout elapsed)`);
  }

  getTimeoutStats(): {
    totalTimeouts: number;
    byErrorCode: Record<string, number>;
    bySeverity: Record<string, number>;
    byNetworkQuality: Record<string, number>;
    recentTimeouts: TimeoutContext[];
    sessionDuration: number;
  } {
    const byErrorCode: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    const byNetworkQuality: Record<string, number> = {};

    this.timeoutHistory.forEach(timeout => {
      byErrorCode[timeout.errorCode] = (byErrorCode[timeout.errorCode] || 0) + 1;
      bySeverity[timeout.severity] = (bySeverity[timeout.severity] || 0) + 1;
      if (timeout.networkQuality) {
        byNetworkQuality[timeout.networkQuality] = (byNetworkQuality[timeout.networkQuality] || 0) + 1;
      }
    });

    return {
      totalTimeouts: this.timeoutHistory.length,
      byErrorCode,
      bySeverity,
      byNetworkQuality,
      recentTimeouts: this.timeoutHistory.slice(-10),
      sessionDuration: Date.now() - this.sessionStartTime,
    };
  }

  printSessionSummary(): void {
    const stats = this.getTimeoutStats();

    console.log('╔════════════════════════════════════════════════════════════════');
    console.log('║ TIMEOUT SESSION SUMMARY');
    console.log('╠════════════════════════════════════════════════════════════════');
    console.log(`║ Session Duration: ${(stats.sessionDuration / 1000 / 60).toFixed(2)} minutes`);
    console.log(`║ Total Timeouts:   ${stats.totalTimeouts}`);
    console.log('╠────────────────────────────────────────────────────────────────');
    console.log('║ By Error Code:');
    Object.entries(stats.byErrorCode).forEach(([code, count]) => {
      console.log(`║   ${code}: ${count}`);
    });
    console.log('╠────────────────────────────────────────────────────────────────');
    console.log('║ By Severity:');
    Object.entries(stats.bySeverity).forEach(([severity, count]) => {
      console.log(`║   ${severity}: ${count}`);
    });
    console.log('╠────────────────────────────────────────────────────────────────');
    console.log('║ By Network Quality:');
    Object.entries(stats.byNetworkQuality).forEach(([quality, count]) => {
      console.log(`║   ${quality}: ${count}`);
    });
    console.log('╚════════════════════════════════════════════════════════════════');
  }

  clearHistory(): void {
    this.timeoutHistory = [];
    this.sessionStartTime = Date.now();
    console.log('🗑️ Timeout history cleared');
  }
}

export const timeoutLogger = new TimeoutLogger();
